import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { CULTURAS_DISPONIVEIS } from '../constants/culturas';
import {
  criarCicloCultivo,
  criarPlantioOperacional,
  editarCicloCultivo,
  editarPlantioOperacional,
  excluirCicloCultivo,
  registrarColheitaCultivo,
  registrarColheitaOperacional,
  statusDoNovoCiclo,
  validarEdicaoCicloCultivo,
  validarNovoCicloCultivo,
  validarRegistroColheita,
} from '../services/ciclosCultivoService';
import './GestaoCultivoModal.css';
import { toLocalIsoDate } from '../utils/localDate';

const hoje = () => toLocalIsoDate();

export default function GestaoCultivoModal({
  open,
  onClose,
  talhao,
  cicloAtual,
  user,
  previewMode = false,
  mode = 'auto',
  onSaved,
  allowDelete = true,
  darkMode = false,
}) {
  const [form, setForm] = useState({
    cultura: '',
    outraCultura: '',
    variedade: '',
    data_plantio: hoje(),
    data_colheita_prevista: '',
    observacoes: '',
  });
  const [dataColheita, setDataColheita] = useState(hoje());
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [error, setError] = useState('');
  const resolvedMode = mode === 'auto' ? (cicloAtual ? 'harvest' : 'create') : mode;
  const isHarvest = resolvedMode === 'harvest';
  const isEdit = resolvedMode === 'edit';
  const isDeleteOnly = resolvedMode === 'delete';
  const isOperational = talhao?._operationalPhysical === true;

  useEffect(() => {
    if (!open) return;
    const culturaCadastrada = String(cicloAtual?.cultura || '').trim();
    const culturaPadrao = CULTURAS_DISPONIVEIS.find(item => (
      item.value.toLocaleLowerCase('pt-BR') === culturaCadastrada.toLocaleLowerCase('pt-BR')
    ));
    setForm({
      cultura: isEdit && culturaCadastrada ? (culturaPadrao?.value || 'outro') : '',
      outraCultura: isEdit && culturaCadastrada && !culturaPadrao ? culturaCadastrada : '',
      variedade: isEdit ? (cicloAtual?.variedade || '') : '',
      data_plantio: isEdit ? (cicloAtual?.data_plantio || hoje()) : hoje(),
      data_colheita_prevista: isEdit ? (cicloAtual?.data_colheita_prevista || '') : '',
      observacoes: isEdit ? (cicloAtual?.observacoes || '') : '',
    });
    setDataColheita(hoje());
    setSaving(false);
    setDeleting(false);
    setConfirmingDelete(false);
    setError('');
  }, [open, talhao?.id, cicloAtual?.id, isEdit]);

  useEffect(() => {
    if (!open || typeof document === 'undefined') return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  if (!open || !talhao || typeof document === 'undefined') return null;

  const culturaSelecionada = form.cultura === 'outro' ? form.outraCultura : form.cultura;
  const updateForm = (field, value) => {
    setForm(current => ({ ...current, [field]: value }));
    setError('');
  };

  const submit = async event => {
    event.preventDefault();
    if (saving) return;

    if (isEdit) {
      const cicloForm = { ...form, cultura: culturaSelecionada };
      const validationError = validarEdicaoCicloCultivo(cicloAtual, cicloForm);
      if (validationError) return setError(validationError);
      setSaving(true);
      try {
        const ciclo = previewMode
          ? {
              ...cicloAtual,
              ...cicloForm,
              status: statusDoNovoCiclo(cicloForm.data_plantio),
            }
          : isOperational
            ? await editarPlantioOperacional({ ciclo: cicloAtual, form: cicloForm })
            : await editarCicloCultivo({ ciclo: cicloAtual, form: cicloForm });
        onSaved?.({ type: 'updated', ciclo });
        onClose?.();
      } catch (saveError) {
        setError(saveError?.message || 'Não foi possível atualizar o plantio.');
      } finally {
        setSaving(false);
      }
      return;
    }

    if (isHarvest) {
      const validationError = validarRegistroColheita(cicloAtual, dataColheita);
      if (validationError) return setError(validationError);
      setSaving(true);
      try {
        const ciclo = previewMode
          ? { ...cicloAtual, status: 'colhido', data_colheita: dataColheita }
          : isOperational
            ? await registrarColheitaOperacional({ ciclo: cicloAtual, dataColheita })
            : await registrarColheitaCultivo({ ciclo: cicloAtual, dataColheita });
        onSaved?.({ type: 'harvested', ciclo });
        onClose?.();
      } catch (saveError) {
        setError(saveError?.message || 'Não foi possível registrar a colheita.');
      } finally {
        setSaving(false);
      }
      return;
    }

    const cicloForm = { ...form, cultura: culturaSelecionada };
    const validationError = validarNovoCicloCultivo(cicloForm, cicloAtual);
    if (validationError) return setError(validationError);
    setSaving(true);
    try {
      const ciclo = previewMode
        ? {
            id: `preview-cycle-${Date.now()}`,
            talhao_id: talhao.id,
            propriedade_id: talhao.propriedade_id,
            user_id: user?.id || 'preview',
            ...cicloForm,
            data_colheita: null,
            status: statusDoNovoCiclo(cicloForm.data_plantio),
            origem: 'preview_local',
          }
        : isOperational
          ? await criarPlantioOperacional({ talhao, form: cicloForm, cicloAtual })
          : await criarCicloCultivo({ talhao, userId: user?.id, form: cicloForm, cicloAtual });
      onSaved?.({ type: 'created', ciclo });
      onClose?.();
    } catch (saveError) {
      setError(saveError?.message || 'Não foi possível cadastrar o plantio.');
    } finally {
      setSaving(false);
    }
  };

  const deletePlanting = async () => {
    if (saving || deleting || (!isEdit && !isDeleteOnly)) return;
    setDeleting(true);
    setError('');
    try {
      const result = await excluirCicloCultivo({ ciclo: cicloAtual });
      onSaved?.({ type: 'deleted', ciclo: cicloAtual, ...result });
      onClose?.();
    } catch (deleteError) {
      setError(deleteError?.message || 'Não foi possível excluir o plantio.');
    } finally {
      setDeleting(false);
    }
  };

  return createPortal(
    <div className={`crop-cycle-backdrop ${darkMode ? 'is-dark' : ''}`} role="presentation" onMouseDown={event => {
      if (event.target === event.currentTarget && !saving) onClose?.();
    }}>
      <section className="crop-cycle-modal" role="dialog" aria-modal="true" aria-labelledby="crop-cycle-title">
        <header>
          <div><span>Talhão permanente</span><h2 id="crop-cycle-title">{isDeleteOnly ? 'Excluir plantio' : isHarvest ? 'Registrar colheita' : isEdit ? 'Editar plantio' : 'Novo plantio'}</h2></div>
          <button type="button" onClick={onClose} disabled={saving} aria-label="Fechar">×</button>
        </header>

        <div className="crop-cycle-context">
          <div><span>Talhão</span><strong>{talhao.nome}</strong></div>
          <div><span>Área fixa</span><strong>{talhao.area ? `${talhao.area} ha` : 'Não informada'}</strong></div>
          <small>O perímetro e a área do talhão não mudam quando um ciclo termina.</small>
        </div>

        <form onSubmit={submit}>
          {isDeleteOnly ? (
            <>
              <div className="crop-cycle-current">
                <span>Plantio selecionado</span>
                <strong>{cicloAtual?.cultura || 'Cultura não informada'}</strong>
                <small>{cicloAtual?.data_plantio ? `Iniciado em ${new Date(`${cicloAtual.data_plantio}T12:00:00`).toLocaleDateString('pt-BR')}` : 'Data de plantio não informada'}</small>
              </div>
              <div className="crop-cycle-delete-confirm" role="alert">
                <strong>Confirme a exclusão do plantio</strong>
                <span>O talhão será mantido. Se existir outro plantio ou qualquer histórico vinculado, o talhão continuará protegido contra exclusão.</span>
              </div>
            </>
          ) : isHarvest ? (
            <>
              <div className="crop-cycle-current">
                <span>Plantio em andamento</span>
                <strong>{cicloAtual.cultura}</strong>
                <small>Iniciado em {new Date(`${cicloAtual.data_plantio}T12:00:00`).toLocaleDateString('pt-BR')}</small>
              </div>
              <label>
                <span>Data efetiva da colheita</span>
                <input type="date" value={dataColheita} min={cicloAtual.data_plantio} onChange={event => { setDataColheita(event.target.value); setError(''); }} required />
              </label>
              <p className="crop-cycle-guidance">Ao confirmar, este ciclo será encerrado e o talhão ficará liberado para um novo plantio.</p>
            </>
          ) : (
            <>
              <label>
                <span>Cultura</span>
                <select value={form.cultura} onChange={event => updateForm('cultura', event.target.value)} required>
                  <option value="">Selecione a cultura...</option>
                  {CULTURAS_DISPONIVEIS.map(cultura => <option key={cultura.value} value={cultura.value}>{cultura.label}</option>)}
                  <option value="outro">Outra cultura...</option>
                </select>
              </label>
              {form.cultura === 'outro' && (
                <label><span>Nome da cultura</span><input value={form.outraCultura} onChange={event => updateForm('outraCultura', event.target.value)} maxLength="80" required /></label>
              )}
              <label><span>Variedade ou material <small>opcional</small></span><input value={form.variedade} onChange={event => updateForm('variedade', event.target.value)} maxLength="100" placeholder="Ex.: clone, cultivar ou híbrido" /></label>
              <div className="crop-cycle-date-row">
                <label><span>Data do plantio</span><input type="date" value={form.data_plantio} onChange={event => updateForm('data_plantio', event.target.value)} required /></label>
                <label><span>Data prevista da colheita <small>opcional</small></span><input type="date" value={form.data_colheita_prevista} min={form.data_plantio || undefined} onChange={event => updateForm('data_colheita_prevista', event.target.value)} /></label>
              </div>
              <label><span>Observações <small>opcional</small></span><textarea value={form.observacoes} onChange={event => updateForm('observacoes', event.target.value)} maxLength="500" rows="3" placeholder="Preparo, finalidade, responsável ou outra informação útil" /></label>
              <p className="crop-cycle-guidance">{isEdit
                ? 'A previsão será aplicada ao mesmo plantio na Central e em Propriedades e Talhões. Para encerrar o ciclo, use a ação Registrar colheita. O perímetro e a área física não serão modificados.'
                : 'Um talhão só pode ter um plantio planejado ou ativo. Para iniciar outro, registre primeiro a colheita deste ciclo.'}</p>
            </>
          )}

          {confirmingDelete && (
            <div className="crop-cycle-delete-confirm" role="alert">
              <strong>Excluir este plantio?</strong>
              <span>O plantio será removido após a confirmação. O talhão permanecerá cadastrado e poderá receber um novo plantio.</span>
            </div>
          )}
          {error && <p className="crop-cycle-error" role="alert">{error}</p>}
          {isDeleteOnly ? (
            <footer>
              <button type="button" onClick={onClose} disabled={deleting}>Cancelar</button>
              <button type="button" className="is-danger" onClick={deletePlanting} disabled={deleting}>
                {deleting ? 'Excluindo…' : 'Excluir plantio'}
              </button>
            </footer>
          ) : confirmingDelete ? (
            <footer>
              <button type="button" onClick={() => { setConfirmingDelete(false); setError(''); }} disabled={deleting}>Voltar</button>
              <button type="button" className="is-danger" onClick={deletePlanting} disabled={deleting}>
                {deleting ? 'Excluindo…' : 'Sim, excluir plantio'}
              </button>
            </footer>
          ) : (
            <footer className={isEdit && allowDelete && !previewMode ? 'has-delete-action' : ''}>
              {isEdit && allowDelete && !previewMode && (
                <button type="button" className="is-danger-outline" onClick={() => setConfirmingDelete(true)} disabled={saving}>
                  Excluir plantio
                </button>
              )}
              <button type="button" onClick={onClose} disabled={saving}>Cancelar</button>
              <button type="submit" className="is-primary" disabled={saving}>{saving ? 'Salvando…' : isHarvest ? 'Confirmar colheita' : isEdit ? 'Salvar alterações' : 'Cadastrar plantio'}</button>
            </footer>
          )}
        </form>
      </section>
    </div>,
    document.body
  );
}
